import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type {
  PipelineFilters,
  PipelineListResponse,
  PipelineRecord,
  PipelineStagesResponse,
  PipelineRepSummaryResponse,
  StageMoveResult,
} from '../types';

/**
 * The unified Sales Pipeline API.
 *
 * One surface for what used to be two menus. `/api/pipeline` is additive — the
 * old `/api/leads` and `/api/deals` endpoints still answer, because the models
 * behind them are now aliases of the same collection — but everything in this
 * feature talks to the unified one so there is a single place where the merged
 * shape is understood.
 */

const KEY = ['pipeline'] as const;

export const pipelineKeys = {
  root: KEY,
  stages: (params?: { owner?: string; startDate?: string; endDate?: string }) =>
    [...KEY, 'stages', params?.owner || 'all', params?.startDate || '', params?.endDate || ''] as const,
  reps: (params?: { startDate?: string; endDate?: string }) =>
    [...KEY, 'reps', params?.startDate || '', params?.endDate || ''] as const,
  list: (f: PipelineFilters) => [...KEY, 'list', f] as const,
  one: (id: string) => [...KEY, 'record', id] as const,
};

function toQuery(f: PipelineFilters): string {
  const p = new URLSearchParams();
  if (f.search) p.set('search', f.search);
  if (f.stage) p.set('stage', f.stage);
  if (f.lostReason) p.set('lostReason', f.lostReason);
  if (f.side) p.set('side', f.side);
  if (f.status) p.set('status', f.status);
  if (f.source) p.set('source', f.source);
  if (f.owner) p.set('owner', f.owner);
  if (f.startDate) p.set('startDate', f.startDate);
  if (f.endDate) p.set('endDate', f.endDate);
  if (f.branchId && f.branchId !== 'all') p.set('branchId', f.branchId);
  if (f.open) p.set('open', '1');
  if (f.beforeStage) p.set('beforeStage', f.beforeStage);
  if (f.mine) p.set('mine', '1');
  if (f.pendingReassignment) p.set('pendingReassignment', '1');
  if (f.sort) p.set('sort', f.sort);
  p.set('limit', String(f.limit));
  p.set('offset', String(f.offset));
  return p.toString();
}

/**
 * The stage list with live counts — what the Kanban columns render from.
 *
 * The stages come from the server rather than a constant in the bundle, because
 * the list is admin-editable in Master Data. A hardcoded copy would show the old
 * wording until the next deploy.
 */
export function usePipelineStages(params?: { owner?: string; startDate?: string; endDate?: string }) {
  const query = new URLSearchParams();
  if (params?.owner) query.set('owner', params.owner);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  const qStr = query.toString();

  return useQuery({
    queryKey: pipelineKeys.stages(params),
    queryFn: async () =>
      (await api.get<PipelineStagesResponse>(`/api/pipeline/stages${qStr ? `?${qStr}` : ''}`)).data,
  });
}

/**
 * High-level employee pipeline monitor metrics for Super Admin, Admin, and Department Admin.
 */
export function usePipelineRepSummary(params?: { startDate?: string; endDate?: string }, enabled = true) {
  const query = new URLSearchParams();
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  const qStr = query.toString();

  return useQuery({
    queryKey: pipelineKeys.reps(params),
    queryFn: async () =>
      (await api.get<PipelineRepSummaryResponse>(`/api/pipeline/rep-summary${qStr ? `?${qStr}` : ''}`)).data,
    enabled,
    staleTime: 60 * 1000,
  });
}

export function usePipelineList(filters: PipelineFilters) {
  return useQuery({
    queryKey: pipelineKeys.list(filters),
    queryFn: async () =>
      (await api.get<PipelineListResponse>(`/api/pipeline?${toQuery(filters)}`)).data,
    placeholderData: (prev) => prev,
  });
}

export function usePipelineRecord(id: string | null) {
  return useQuery({
    enabled: !!id,
    queryKey: pipelineKeys.one(id || ''),
    queryFn: async () => (await api.get<PipelineRecord>(`/api/pipeline/${id}`)).data,
  });
}

export function usePipelineMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: KEY });

  const create = useMutation({
    // `confirmDuplicate` answers the server's DUPLICATE_OPEN_RECORD warning (a
    // recent open record for the same client) — see NewPipelineRecordPage.
    mutationFn: async (payload: Partial<PipelineRecord> & { confirmDuplicate?: boolean }) =>
      (await api.post<{ ok: boolean; id: string }>('/api/pipeline', payload)).data,
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: Partial<PipelineRecord> }) =>
      (await api.patch(`/api/pipeline/${id}`, payload)).data,
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => (await api.delete<{ ok: boolean }>(`/api/pipeline/${id}`)).data,
    onSuccess: invalidate,
  });

  /**
   * Move to Trash — the safe, reversible alternative to Delete above. Unlike
   * Delete, the server does not block this when a quotation already exists:
   * trashing hides that quotation (and the client's profile, if nothing else
   * still needs it) right along with the record, rather than orphaning it.
   * Only a Super Admin can see the Trash afterwards, restore it, or
   * permanently delete it (usePipelineTrash.ts).
   */
  const moveToTrash = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) =>
      (await api.post<{ ok: boolean }>(`/api/pipeline/${id}/trash`, { reason })).data,
    onSuccess: invalidate,
  });

  /**
   * Move a record's stage by hand.
   *
   * Closing as lost requires a reason — the server refuses without one, which is
   * why `lostReasonNote` is part of the call rather than something the UI can
   * forget. A lost-reason report whose largest bucket is "unspecified" answers
   * nothing.
   */
  const moveStage = useMutation({
    mutationFn: async ({ id, stage, lostReasonId, lostReasonNote, moveNote, followup }: {
      id: string;
      stage: string;
      lostReasonId?: string | null;
      lostReasonNote?: string;
      // Required by the server when the move is a genuine regression (moving
      // to an earlier stage than the record is already at, including
      // reopening a closed record) — see validateManualStageChange in
      // src/pipeline-policy.js. Not needed for a forward move or for
      // closing as lost, which has its own lostReasonNote.
      moveNote?: string;
      // Item 44 — the stage route already accepts a follow-up and persists it
      // (src/api-pipeline/index.js passes it to moveStageManually). Dropping it
      // here meant a follow-up scheduled from the outcome dialog silently went
      // nowhere whenever that dialog also moved the stage — which is most of
      // the time, since Qualified/Contacted moves come from the same dialog.
      followup?: Record<string, unknown>;
    }) =>
      (await api.patch<StageMoveResult>(`/api/pipeline/${id}/stage`, {
        stage, lostReasonId, lostReasonNote, moveNote, ...(followup ? { followup } : {}),
      })).data,
    onSuccess: invalidate,
  });

  /**
   * Approve or reject a pending cross-BDM reassignment request — admin-tier
   * only (server-enforced too). Approve moves the record to the contact's
   * real owner; reject leaves it with whoever originally created it.
   */
  const decideReassignment = useMutation({
    mutationFn: async ({ id, decision }: { id: string; decision: 'approve' | 'reject' }) =>
      (await api.post<{ ok: boolean; decision: string; assignedTo: string }>(`/api/pipeline/${id}/reassignment/${decision}`)).data,
    onSuccess: invalidate,
  });

  return { create, update, remove, moveToTrash, moveStage, decideReassignment };
}

/** Narrow a stage-move failure to its message. */
export function stageErrorText(err: unknown, fallback = 'Could not move the record.'): string {
  const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
  return msg || fallback;
}
